import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { FIELD_TYPES } from '../utils/fieldTypes';

const emptyField = () => ({
  id: `f_${Date.now()}_${Math.random().toString(36).slice(2,5)}`,
  label: '', type: 'text', required: false, scope: 'member', placeholder: '', options: [],
});

const emptyPack = () => ({ name: '', description: '', category: '', fields: [], isDefault: false });

// ── Editor de un campo dentro del paquete ───────────────────
function FieldEditor({ field, index, total, onChange, onRemove, onMove }) {
  const [optInput, setOptInput] = useState((field.options || []).join('\n'));
  return (
    <div style={{ display: 'flex', gap: '.6rem', padding: '.7rem .9rem', background: 'rgba(255,255,255,.02)', borderRadius: 8, border: '1px solid rgba(122,171,138,.08)', marginBottom: '.4rem' }}>
      <div style={{ width: 28, height: 28, background: 'rgba(122,171,138,.1)', border: '1px solid rgba(122,171,138,.2)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.78rem', color: 'var(--sage)', flexShrink: 0, marginTop: 2 }}>
        {{ text:'✎', textarea:'≡', number:'#', date:'📅', select:'▾', boolean:'◉', email:'@', phone:'📱' }[field.type]||'✎'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="form-row-2" style={{ marginBottom: '.3rem' }}>
          <input className="form-input" value={field.label} placeholder="Nombre del campo *"
            onChange={e => onChange({ ...field, label: e.target.value })} />
          <select className="form-select" value={field.type}
            onChange={e => onChange({ ...field, type: e.target.value, options: [] })}>
            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-select" value={field.scope || 'member'}
            onChange={e => onChange({ ...field, scope: e.target.value })}
            style={{ width: 'auto', fontSize: '.78rem', padding: '.25rem .5rem' }}>
            <option value="member">Por persona</option>
            <option value="family">De grupo</option>
          </select>
          <label className="toggle-wrap">
            <input type="checkbox" checked={!!field.required}
              onChange={e => onChange({ ...field, required: e.target.checked })} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: '.78rem' }}>Obligatorio</span>
          </label>
        </div>
        {field.type === 'select' && (
          <textarea className="form-textarea" value={optInput} placeholder="Una opción por línea"
            onChange={e => { setOptInput(e.target.value); onChange({ ...field, options: e.target.value.split('\n').filter(Boolean) }); }}
            style={{ marginTop: '.4rem', minHeight: 55, fontSize: '.82rem' }} />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.2rem', flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" style={{ padding: '.2rem .35rem' }} onClick={() => onMove(index, -1)} disabled={index === 0}>↑</button>
        <button className="btn btn-ghost btn-sm" style={{ padding: '.2rem .35rem' }} onClick={() => onMove(index, 1)} disabled={index === total - 1}>↓</button>
        <button className="btn btn-danger btn-sm" style={{ padding: '.2rem .35rem' }} onClick={() => onRemove(field.id)}>✕</button>
      </div>
    </div>
  );
}

// ── Modal de crear/editar paquete ────────────────────────────
function PackModal({ pack, categories, onClose, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState(pack ? { ...pack, fields: pack.fields || [] } : emptyPack());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newCat, setNewCat] = useState('');
  const isNew = !pack?.id;

  const addField = () => setForm(p => ({ ...p, fields: [...p.fields, emptyField()] }));
  const removeField = (id) => setForm(p => ({ ...p, fields: p.fields.filter(f => f.id !== id) }));
  const updateField = (updated) => setForm(p => ({ ...p, fields: p.fields.map(f => f.id === updated.id ? updated : f) }));
  const moveField = (i, dir) => {
    const arr = [...form.fields];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setForm(p => ({ ...p, fields: arr }));
  };

  const save = async () => {
    if (!form.name.trim()) { setError('El nombre del paquete es obligatorio'); return; }
    if (form.fields.some(f => !f.label.trim())) { setError('Todos los campos deben tener nombre'); return; }
    setSaving(true);
    try {
      if (isNew) await api.request('POST', '/api/admin/question-packs', form, token);
      else await api.request('PATCH', `/api/admin/question-packs/${pack.id}`, form, token);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const allCats = [...new Set([...categories, form.category].filter(Boolean))];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <div>
            <div className="section-label">{isNew ? 'Nuevo paquete' : 'Editar paquete'}</div>
            <h2>{isNew ? 'Crear paquete de preguntas' : form.name}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

        <div className="form-row-2">
          <div className="form-group">
            <label className="form-label">Nombre del paquete <span className="req">*</span></label>
            <input className="form-input" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Ej: Datos médicos, Datos deportivos..." />
          </div>
          <div className="form-group">
            <label className="form-label">Categoría</label>
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <select className="form-select" value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                <option value="">— Sin categoría —</option>
                {allCats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="form-input" value={newCat} placeholder="Nueva..."
                onChange={e => setNewCat(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newCat.trim()) { setForm(p => ({ ...p, category: newCat.trim() })); setNewCat(''); } }}
                style={{ width: 110 }} />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Descripción</label>
          <input className="form-input" value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Para qué sirve este paquete..." />
        </div>

        <div className="form-group" style={{ marginBottom: '.6rem' }}>
          <label className="toggle-wrap">
            <input type="checkbox" checked={!!form.isDefault}
              onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: '.88rem', fontWeight: 600 }}>Incluir por defecto en nuevos eventos</span>
          </label>
          <div className="form-hint" style={{ marginLeft: '3.2rem' }}>
            Los paquetes marcados como "por defecto" se añadirán automáticamente al formulario de cada nuevo evento
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.6rem' }}>
          <div className="section-label" style={{ marginBottom: 0, flex: 1 }}>
            Campos del paquete ({form.fields.length})
          </div>
          <button className="btn btn-secondary btn-sm" onClick={addField}>+ Añadir campo</button>
        </div>

        {form.fields.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--mid)', fontStyle: 'italic', fontSize: '.88rem', background: 'rgba(255,255,255,.02)', borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.1)', marginBottom: '1rem' }}>
            No hay campos. Pulsa "Añadir campo" para empezar.
          </div>
        ) : (
          <div style={{ marginBottom: '1rem' }}>
            {form.fields.map((f, i) => (
              <FieldEditor key={f.id} field={f} index={i} total={form.fields.length}
                onChange={updateField} onRemove={removeField} onMove={moveField} />
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '.8rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: 'var(--border)' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><span className="spinner" /> Guardando...</> : 'Guardar paquete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────
export default function QuestionPacks() {
  const { token } = useAuth();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    api.request('GET', '/api/admin/question-packs', null, token)
      .then(d => setPacks(d.packs || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const deletePack = async (id) => {
    if (!window.confirm('¿Eliminar este paquete? No afectará a los eventos que ya lo usen.')) return;
    try {
      await api.request('DELETE', `/api/admin/question-packs/${id}`, null, token);
      load();
    } catch (e) { setError(e.message); }
  };

  const onSaved = () => { setEditing(null); setCreating(false); load(); };

  const categories = [...new Set(packs.map(p => p.category).filter(Boolean))];
  const filtered = filterCat ? packs.filter(p => p.category === filterCat) : packs;

  if (loading) return <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}><div className="spinner spinner-lg" /></div>;

  return (
    <div className="main-content page-enter">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.8rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div className="section-label">Administración</div>
          <h1>Paquetes de preguntas</h1>
          <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
            Crea grupos de preguntas reutilizables para los formularios de los eventos
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Nuevo paquete</button>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {/* Filtro por categoría */}
      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
          <button className={`btn btn-sm ${!filterCat ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilterCat('')}>Todos</button>
          {categories.map(c => (
            <button key={c} className={`btn btn-sm ${filterCat === c ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilterCat(c)}>{c}</button>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)', fontStyle: 'italic' }}>
          No hay paquetes de preguntas. Crea uno para empezar.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
        {filtered.map(pack => (
          <div key={pack.id} className="card" style={{ padding: '1rem 1.2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.8rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1rem', fontWeight: 700 }}>{pack.name}</div>
                  {pack.category && <span className="badge badge-moss">{pack.category}</span>}
                  {pack.isDefault && <span className="badge badge-amber" style={{ fontSize: '.6rem' }}>⚡ Por defecto</span>}
                  <span className="badge badge-stone">{pack.fields?.length || 0} campos</span>
                </div>
                {pack.description && (
                  <div style={{ fontSize: '.82rem', color: 'var(--mid)', marginTop: '.3rem' }}>{pack.description}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '.4rem', flexShrink: 0 }}>
                <button className="btn btn-ghost btn-sm"
                  onClick={() => setExpanded(p => ({ ...p, [pack.id]: !p[pack.id] }))}>
                  {expanded[pack.id] ? '▲ Ocultar' : '▼ Ver campos'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(pack)}>Editar</button>
                <button className="btn btn-danger btn-sm" onClick={() => deletePack(pack.id)}>Eliminar</button>
              </div>
            </div>

            {/* Campos expandidos */}
            {expanded[pack.id] && pack.fields?.length > 0 && (
              <div style={{ marginTop: '.8rem', borderTop: '1px solid rgba(122,171,138,.1)', paddingTop: '.8rem' }}>
                {pack.fields.map((f, i) => (
                  <div key={f.id || i} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', padding: '.45rem .6rem', borderRadius: 6, background: 'rgba(255,255,255,.02)', marginBottom: '.3rem' }}>
                    <span style={{ fontSize: '.78rem', color: 'var(--sage)', width: 18, textAlign: 'center' }}>
                      {{ text:'✎', textarea:'≡', number:'#', date:'📅', select:'▾', boolean:'◉', email:'@', phone:'📱' }[f.type]||'✎'}
                    </span>
                    <span style={{ fontSize: '.85rem', fontWeight: 500, flex: 1 }}>{f.label}</span>
                    <span style={{ fontSize: '.7rem', color: 'var(--mid)' }}>
                      {FIELD_TYPES.find(t => t.value === f.type)?.label}
                      {f.required && ' · Obligatorio'}
                      {f.scope === 'family' && ' · Grupo'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {creating && <PackModal categories={categories} onClose={() => setCreating(false)} onSaved={onSaved} />}
      {editing && <PackModal pack={editing} categories={categories} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
